import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GenericForm, GenericFormField } from "@/styles/GenericForm";
import { GenericAttachmentsSection, GenericAttachment } from "@/styles/GenericAttachmentsSection";
import { DailyLog, DailyLogAttachment, NewDailyLogInput, WeatherMode } from "../types";
import { fetchWeatherForLocation, formatWeatherText, geocodeAddressToCoords, buildFullAddress } from "../weatherAPI";
import { fetchProjectLookup } from "@/lib/entity-api";
import { uploadDailyLogFiles, deleteDailyLogFile } from "../store";
import { useUser } from "@/contexts/UserContext";
import { useProject, getDefaultProjectIdForNewRecord } from "@/components/ProjectSelector";
import { useAttachmentStaging } from "@/hooks/useAttachmentStaging";

interface DailyLogModalProps {
  open: boolean;
  mode: "create" | "edit";
  initial?: DailyLog | null;
  onClose: () => void;
  onSubmit: (data: NewDailyLogInput) => Promise<DailyLog | undefined>;
  projects?: { id: string; name: string }[];
}

// DailyLogAttachment <-> GenericAttachment are the same data under different
// field names; these convert between the store's shape and the shape
// GenericAttachmentsSection/useAttachmentStaging expect.
const toGenericAttachment = (a: DailyLogAttachment): GenericAttachment => ({
  id: a.id,
  file_name: a.name,
  file_type: a.type || 'file',
  file_size: a.size || 0,
  file_url: a.url,
  created_at: a.createdAt || new Date().toISOString(),
  created_by: '',
  created_by_user: undefined,
});

const toDailyLogAttachment = (a: GenericAttachment): DailyLogAttachment => ({
  id: a.id,
  name: a.file_name,
  type: a.file_type,
  url: a.file_url,
  size: a.file_size,
  createdAt: a.created_at,
});

export function DailyLogModal({ open, mode, initial, onClose, onSubmit, projects = [] }: DailyLogModalProps) {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  const [weatherMode, setWeatherMode] = useState<WeatherMode>("auto");
  const [weatherText, setWeatherText] = useState<string>("");
  const [weatherLoading, setWeatherLoading] = useState<boolean>(false);
  const [weatherError, setWeatherError] = useState<string | null>(null);
  const [projectAddress, setProjectAddress] = useState<string>("");
  const [windKph, setWindKph] = useState<number | undefined>(undefined);
  const [humidityPercent, setHumidityPercent] = useState<number | undefined>(undefined);
  const [precipitationMm, setPrecipitationMm] = useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadDailyLogAttachmentsFn = useCallback(async (entityId: string, filesToUpload: File[]): Promise<GenericAttachment[]> => {
    const uploaded = await uploadDailyLogFiles(entityId, filesToUpload, companyId ?? '');
    return uploaded.map(toGenericAttachment);
  }, [companyId]);

  const deleteDailyLogAttachmentFn = useCallback(async (attachmentId: string): Promise<boolean> => {
    return deleteDailyLogFile(attachmentId);
  }, []);

  const {
    pendingFiles: files,
    uploadedFiles: uploadedAttachments,
    setUploadedFiles: setUploadedAttachments,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemovePendingFile,
    handleDelete: handleDeleteFileBase,
    resetPending: resetFiles,
    uploadPending: uploadPendingFiles,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: undefined, // always stage; this form defers ALL uploads to Submit time, even in edit mode
    uploadFn: uploadDailyLogAttachmentsFn,
    deleteFn: deleteDailyLogAttachmentFn,
  });

  const handleDeleteFile = useCallback(async (attachmentId: string) => {
    if (window.confirm('Are you sure you want to delete this attachment?')) {
      const ok = await handleDeleteFileBase(attachmentId);
      if (!ok) alert('Failed to delete attachment. Please try again.');
    }
  }, [handleDeleteFileBase]);

  // Initialize local state when modal opens
  useEffect(() => {
    if (open) {
      if (mode === "edit" && initial) {
        resetFiles();
        setUploadedAttachments(initial.attachments.map(toGenericAttachment));
        setWeatherMode(initial.weatherMode);
        setWeatherText(initial.weatherText ?? "");
        setWindKph(initial.windKph);
        setHumidityPercent(initial.humidityPercent);
        setPrecipitationMm(initial.precipitationMm);
      } else {
        // create mode: reset
        resetFiles();
        setUploadedAttachments([]);
        setWeatherMode("auto");
        setWeatherText("");
        setProjectAddress("");
        setWindKph(undefined);
        setHumidityPercent(undefined);
        setPrecipitationMm(undefined);
      }
    }
  }, [open, mode, initial, resetFiles, setUploadedAttachments]);

  // Compute initial values for GenericForm directly from props
  const initialValues = useMemo(() => {
    if (mode === "edit" && initial) {
      return {
        date: initial.date.slice(0, 10),
        projectId: initial.projectId,
        notes: initial.notes ?? "",
        issues: initial.issues ?? "",
        weatherMode: initial.weatherMode,
        weatherText: initial.weatherText ?? "",
        visibleToClient: initial.visibleToClient === true,
      };
    }
    const today = new Date();
    const y = today.getFullYear();
    const m = `${today.getMonth() + 1}`.padStart(2, '0');
    const d = `${today.getDate()}`.padStart(2, '0');
    return {
      date: `${y}-${m}-${d}`,
      projectId: getDefaultProjectIdForNewRecord(selectedProject),
      notes: "",
      issues: "",
      weatherMode: "auto",
      weatherText: "",
      visibleToClient: false,
    };
  }, [mode, initial, selectedProject]);

  // Function to fetch project address
  const fetchProjectAddress = React.useCallback(async (projectId: string) => {
    if (!open || !projectId) {
        setProjectAddress("");
      if (weatherMode === "auto") {
        setWeatherText("");
        setWindKph(undefined);
        setHumidityPercent(undefined);
        setPrecipitationMm(undefined);
      }
        return;
      }
      try {
        if (!companyId) return;
        // The project's address comes with the projects lookup (API); nothing is read from Supabase here.
        const data = (await fetchProjectLookup()).find((p) => p.id === projectId);
        if (data) {
          const full = buildFullAddress([data.address, data.city, data.state, data.zip_code]);
          setProjectAddress(full);
        if (!full && weatherMode === "auto") {
            setWeatherText("");
            setWindKph(undefined);
            setHumidityPercent(undefined);
            setPrecipitationMm(undefined);
          }
        } else {
          setProjectAddress("");
        if (weatherMode === "auto") {
          setWeatherText("");
          setWindKph(undefined);
          setHumidityPercent(undefined);
          setPrecipitationMm(undefined);
        }
        }
      } catch (err) {
        setProjectAddress("");
      if (weatherMode === "auto") {
        setWeatherText("");
        setWindKph(undefined);
        setHumidityPercent(undefined);
        setPrecipitationMm(undefined);
      }
    }
  }, [open, companyId, weatherMode]);

  // Fetch project address when form opens with initial projectId
  useEffect(() => {
    if (open && initialValues.projectId) {
      fetchProjectAddress(initialValues.projectId);
    }
  }, [open, initialValues.projectId, fetchProjectAddress]);

  // Auto-fetch weather when weatherMode is "auto" and projectAddress is available
  useEffect(() => {
    async function run() {
      if (!open) return;
      if (weatherMode !== "auto") return;
      if (!projectAddress) return;
      try {
        setWeatherLoading(true);
        setWeatherError(null);
        const coords = await geocodeAddressToCoords(projectAddress);
        const result = await fetchWeatherForLocation(coords);
        setWeatherText(formatWeatherText(result));
        setWindKph(result.windKph);
        setHumidityPercent(result.humidityPercent);
        setPrecipitationMm(result.precipitationMm);
      } catch (err: any) {
        setWeatherError(err?.message || "Failed to fetch weather");
        setWeatherText("");
        setWindKph(undefined);
        setHumidityPercent(undefined);
        setPrecipitationMm(undefined);
      } finally {
        setWeatherLoading(false);
      }
    }
    run();
  }, [open, weatherMode, projectAddress]);

  // Define form fields
  const dailyLogFields = useMemo<GenericFormField[]>(() => [
    { name: 'date', label: 'Date', type: 'date', required: true, row: 1 },
    {
      name: 'projectId',
      label: 'Project',
      type: 'select',
      required: true,
      row: 1,
      options: projects.map(p => ({ label: p.name, value: p.id }))
    },
    { name: 'notes', label: 'Notes', type: 'textarea', row: 2, colSpan: 2, placeholder: 'Enter notes' },
    { name: 'issues', label: 'Issues', type: 'textarea', row: 3, colSpan: 2, placeholder: 'Enter issues' },
    {
      name: 'weatherMode',
      label: 'Weather Mode',
      type: 'select',
      row: 4,
      options: [
        { label: 'Auto', value: 'auto' },
        { label: 'Manual', value: 'none' },
      ],
      helperText: weatherLoading ? "Fetching weather…" : weatherError ? (
        <span className="text-red-600">Error: {weatherError}</span>
      ) : "If Auto is selected, this will be filled automatically."
    },
    { 
      name: 'weatherText', 
      label: 'Weather Description', 
      type: 'text', 
      row: 4, 
      placeholder: 'e.g., Sunny, high 28°C low 26°C',
      disabled: weatherMode === 'auto'
    },
    { name: 'visibleToClient', label: 'Visible to client', type: 'checkbox', row: 5, colSpan: 2, helperText: 'The client of this project can read this log (and only the logs marked this way).' },
  ], [projects, weatherMode]);

  const handleFieldChange = (name: string, value: any, updatedFormData: Record<string, any>) => {
    // Handle weatherMode change
    if (name === 'weatherMode') {
      const newMode = value as WeatherMode;
      setWeatherMode(newMode);
      if (newMode === 'none') {
        setWindKph(undefined);
        setHumidityPercent(undefined);
        setPrecipitationMm(undefined);
      }
    }

    // Handle weatherText change
    if (name === 'weatherText') {
      setWeatherText(value);
    }

    // Fetch project address when projectId changes
    if (name === 'projectId' && updatedFormData.projectId) {
      fetchProjectAddress(updatedFormData.projectId);
    }
  };

  const handleSubmit = async (data: Record<string, any>) => {
    if (isSubmitting) return;
    
    // Validate required fields
    if (!data.date || !data.projectId) {
      setError('Date and Project are required');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const payload: NewDailyLogInput = {
        date: data.date,
        projectId: data.projectId,
        notes: data.notes || undefined,
        issues: data.issues || undefined,
        visibleToClient: data.visibleToClient === true,
        attachments: mode === "edit" ? uploadedAttachments.map(toDailyLogAttachment) : [],
      weatherMode,
      weatherText,
      windKph: weatherMode === "none" ? undefined : windKph,
      humidityPercent: weatherMode === "none" ? undefined : humidityPercent,
      precipitationMm: weatherMode === "none" ? undefined : precipitationMm,
    };

      const result = await onSubmit(payload);

      if (result && result.id) {
        await uploadPendingFiles(result.id);
    }

    onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to save daily log');
      console.error('Error submitting daily log:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Attachments section component
  // Browse Drive follows the form's own Project field
  const attachmentsSection = (projectId: string) => (
    <GenericAttachmentsSection
      projectId={projectId}
      attachments={uploadedAttachments}
      pendingFiles={files}
      onUpload={handleFileUpload}
      onDelete={handleDeleteFile}
      onRemovePending={handleRemovePendingFile}
      mode={mode}
    />
  );


  return (
    <GenericForm
      title="Daily Log"
      mode={mode}
      fields={dailyLogFields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={onClose}
      isOpen={open}
      additionalSections={(values) => attachmentsSection(values.projectId || '')}
      error={error}
      submitButtonText="Save Changes"
      saving={isSubmitting}
      onFieldChange={handleFieldChange}
    />
  );
}
